import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { showSpinner, hideSpinner } from 'src/app/core/services/functions.service';
import { NotificationHelper } from 'src/app/core/services/helpers/notification.helper';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { DetalleNovedadDialogComponent } from '../detalle-novedad-dialog/detalle-novedad-dialog.component';
import { NovedadesComercialesService, NovedadComercial } from '../../services/novedades-comerciales.service';
import { PeriodosmetasService, PeriodoMetas } from '../../../periodosmetas/services/periodosmetas.service';
import { VendedorService, Vendedor } from '../../../metavendedor/services/vendedor.service';
import { TipoNovedadService, TipoNovedad } from '../../services/tipo-novedad.service';
import { finalize } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { OpcionesCargaDialogComponent } from '../opciones-carga-dialog/opciones-carga-dialog.component';

interface ExcelRow {
  periodo: string | number;
  c_vend: string;
  c_tiponovedad: string | number;
  dias: number;
  observaciones: string | number;
}

interface ResultadoCarga {
  periodo: string;
  c_tiponovedad: string;
  c_vend: string;
  dias: number;
  estado: string;
  mensaje: string;
}

interface NovedadComercialBulkLoad {
  periodo: string;
  c_vend: string;
  c_tiponovedad: string;
  dias: number;
  observaciones: string;
}

@Component({
  selector: 'app-novedadescomerciales',
  templateUrl: './novedadescomerciales.component.html',
  styleUrls: ['./novedadescomerciales.component.scss']
})
export class NovedadescomercialesComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  novedadForm: FormGroup;
  searchControl = new FormControl('');
  vendedorControl = new FormControl('');
  isEditing = false;
  isLoading = false;
  editIndex: number | null = null;
  selectedFile: File | null = null;

  // Datos para los selects
  periodosAbiertos: PeriodoMetas[] = [];
  vendedores: Vendedor[] = [];
  vendedoresFiltrados: Observable<Vendedor[]>;
  tiposNovedad: TipoNovedad[] = [];

  displayedColumns: string[] = ['periodo', 'c_tiponovedad', 'c_ven', 'dias', 'acciones'];
  dataSource = new MatTableDataSource<NovedadComercial>();

  // Campos para la tabla de instrucciones
  camposRequeridos = [
    { campo: 'periodo', formato: 'YYYYMM', ejemplo: '202501', descripcion: 'Periodo de la novedad' },
    { campo: 'c_vend', formato: 'Texto', ejemplo: 'V001', descripcion: 'Código del vendedor' },
    { campo: 'c_tiponovedad', formato: 'Texto', ejemplo: 'NOV01', descripcion: 'Código del tipo de novedad' },
    { campo: 'observacion', formato: 'Texto', ejemplo: 'Descripción de la novedad', descripcion: 'Observaciones o detalles' },
    { campo: 'dias', formato: 'Número', ejemplo: '10', descripcion: 'Cantidad de días' }
  ];
  columnasTabla = ['campo', 'formato', 'ejemplo', 'descripcion'];

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private notificationService: NotificationHelper,
    private novedadesComercialesService: NovedadesComercialesService,
    private periodosService: PeriodosmetasService,
    private vendedorService: VendedorService,
    private tipoNovedadService: TipoNovedadService
  ) {

    this.initForm();
    this.configurarFiltroVendedores();

    // Suscribirse a los cambios del vendedor seleccionado
    this.vendedorControl.valueChanges.subscribe(vendedor => {
      if (vendedor && typeof vendedor === 'object') {
        this.novedadForm.patchValue({
          c_ven: vendedor.codigo_vendedor
        });
      } else {
        this.novedadForm.patchValue({
          c_ven: null
        });
      }
    });
  }

  ngOnInit(): void {
    this.cargarPeriodosAbiertos();
    this.cargarTiposNovedad();
    this.cargarVendedores();
    this.cargarNovedades();
  }

  ngAfterViewInit() {
    if (this.dataSource) {
      this.dataSource.paginator = this.paginator;
    }
  }

  initForm(): void {
    this.novedadForm = this.fb.group({
      periodo: ['', Validators.required],
      c_tiponovedad: ['', Validators.required],
      c_ven: ['', Validators.required],
      dias: ['', [Validators.required, Validators.min(1)]],
      observaciones: ['']
    });
  }

  cargarPeriodosAbiertos(): void {
    showSpinner();
    this.periodosService.getAllPeriodos('JA')
      .pipe(finalize(() => hideSpinner()))
      .subscribe({
        next: (response: any) => {
          if (Array.isArray(response)) {
            // Filtrar solo los periodos abiertos
            this.periodosAbiertos = response
              .filter((periodo: any) => periodo.estadoperiodo === 'ABIERTO')
              .map((periodo: any) => ({
                ...periodo
              }));
          } else if (response && response.data && Array.isArray(response.data)) {
            // Filtrar solo los periodos abiertos
            this.periodosAbiertos = response.data
              .filter((periodo: any) => periodo.estadoperiodo === 'ABIERTO')
              .map((periodo: any) => ({
                ...periodo
              }));
          } else {
            this.periodosAbiertos = [];
          }
        },
        error: (error) => {
          this.notificationService.errorMessage('Error al cargar los periodos: ' + error.message);
          this.periodosAbiertos = [];
        }
      });
  }

  cargarTiposNovedad(): void {
    showSpinner();
    this.tipoNovedadService.getTiposNovedad('JA')
      .pipe(finalize(() => hideSpinner()))
      .subscribe({
        next: (tipos) => {
          this.tiposNovedad = tipos;
        },
        error: (error) => {
          this.notificationService.errorMessage('Error al cargar los tipos de novedad: ' + error.message);
        }
      });
  }

  cargarVendedores(): void {
    showSpinner();
    this.vendedorService.getVendedores('JA')
      .pipe(finalize(() => hideSpinner()))
      .subscribe({
        next: (vendedores) => {
          this.vendedores = vendedores;
          this.configurarFiltroVendedores();
        },
        error: (error) => {
          this.notificationService.errorMessage('Error al cargar los vendedores: ' + error.message);
        }
      });
  }

  configurarFiltroVendedores(): void {
    this.vendedoresFiltrados = this.vendedorControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        const nombre = typeof value === 'string' ? value : value?.nombre_vendedor;
        return nombre ? this._filtrarVendedores(nombre) : this.vendedores.slice();
      })
    );
  }

  private _filtrarVendedores(nombre: string): Vendedor[] {
    const filterValue = nombre.toLowerCase();
    return this.vendedores.filter(vendedor =>
      vendedor.nombre_vendedor.toLowerCase().includes(filterValue) ||
      vendedor.codigo_vendedor.toLowerCase().includes(filterValue)
    );
  }

  displayVendedor(vendedor: Vendedor): string {
    return vendedor ? `${vendedor.codigo_vendedor} - ${vendedor.nombre_vendedor}` : '';
  }

  cargarNovedades(): void {
    showSpinner();
    this.novedadesComercialesService.getNovedades('JA')
      .pipe(finalize(() => hideSpinner()))
      .subscribe({
        next: (novedades) => {
          this.dataSource.data = novedades || [];
          if (this.dataSource.paginator) {
            this.dataSource.paginator = this.paginator;
          }
        },
        error: (error) => {
          this.dataSource.data = [];
          if (this.dataSource.paginator) {
            this.dataSource.paginator = this.paginator;
          }
          this.notificationService.errorMessage('Error al cargar las novedades. Por favor, intente nuevamente.');
        }
      });
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (fileExt === 'xlsx' || fileExt === 'xls' || fileExt === 'csv') {
        this.selectedFile = file;
      } else {
        this.notificationService.errorMessage('Por favor seleccione un archivo válido (.xlsx, .xls o .csv)');
        this.selectedFile = null;
      }
    }
  }

  procesarArchivoSeleccionado(replace: boolean = false): void {
    if (!this.selectedFile) {
      this.notificationService.warningMessage('Por favor seleccione un archivo primero');
      return;
    }

    const fileExt = this.selectedFile.name.split('.').pop()?.toLowerCase();
    
    if (fileExt === 'csv') {
      this.procesarCSV(this.selectedFile, replace);
    } else if (fileExt === 'xlsx' || fileExt === 'xls') {
      this.procesarExcel(this.selectedFile, replace);
    }
  }

  private procesarCSV(file: File, replace: boolean): void {
    const reader = new FileReader();
    reader.readAsText(file, "UTF-8");
    reader.onload = (evt) => {
      try {
        const fileContent = evt.target?.result as string;
        const data = this.parseCSV(fileContent);
        this.enviarDatos(data, replace);
      } catch (error) {
        this.notificationService.errorMessage(
          error instanceof Error ? error.message : 'Error al procesar el archivo CSV'
        );
      }
    };
    reader.onerror = () => {
      this.notificationService.errorMessage('Error al leer el archivo CSV');
    };
  }

  private procesarExcel(file: File, replace: boolean): void {
    const reader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = (evt) => {
      try {
        const binaryStr = evt.target?.result;
        const workbook = XLSX.read(binaryStr, { type: 'binary' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const data = this.parseExcel(firstSheet);
        this.enviarDatos(data, replace);
      } catch (error) {
        this.notificationService.errorMessage(
          error instanceof Error ? error.message : 'Error al procesar el archivo Excel'
        );
      }
    };
    reader.onerror = () => {
      this.notificationService.errorMessage('Error al leer el archivo Excel');
    };
  }

  enviarDatos(data: NovedadComercialBulkLoad[], replace: boolean): void {
    if (data.length === 0) {
      this.notificationService.warningMessage('El archivo está vacío');
      return;
    }

    // Si es reemplazo, obtener el periodo del primer registro
    const periodo = replace ? data[0].periodo : undefined;

    this.novedadesComercialesService.bulkLoad('JA',data, replace, periodo)
      .subscribe({
        next: (response) => {
          let mensaje = `Se procesaron ${response.registrosInsertados} registros correctamente.`;
          if (response.registrosFallidos.length > 0) {
            mensaje += ` ${response.registrosFallidos.length} registros fallaron.`;
          }
          this.notificationService.successMessage(mensaje);
          this.cargarNovedades();
          // Limpiar el archivo seleccionado después de procesar
          this.selectedFile = null;
        },
        error: (error) => {
          this.notificationService.errorMessage(error.message);
        }
      });
  }

  private parseExcel(worksheet: XLSX.WorkSheet): NovedadComercialBulkLoad[] {
    const datos: NovedadComercialBulkLoad[] = [];
    const rows = XLSX.utils.sheet_to_json(worksheet, { raw: false });

    // Validar que estén todas las columnas requeridas
    const primeraFila = rows[0] as any;
    const columnasRequeridas = ['periodo', 'c_vend', 'c_tiponovedad', 'dias', 'observaciones'];
    const columnasFaltantes = columnasRequeridas.filter(col => 
      !Object.keys(primeraFila).map(k => k.toLowerCase()).includes(col.toLowerCase())
    );

    if (columnasFaltantes.length > 0) {
      throw new Error(`El archivo debe contener las columnas: ${columnasRequeridas.join(', ')}`);
    }

    // Procesar cada fila
    rows.forEach((row: any, index: number) => {
      const periodo = String(row.periodo || '').trim();
      const c_vend = String(row.c_vend || '').trim().toUpperCase();
      const c_tiponovedad = String(row.c_tiponovedad || '').trim().toUpperCase();
      const dias = Number(row.dias);
      const observaciones = String(row.observaciones || '').trim();

      // Validar los datos
      if (!periodo || !c_vend || !c_tiponovedad || !dias) {
        throw new Error(`Fila ${index + 2}: Faltan campos requeridos`);
      }

      if (!periodo.match(/^\d{6}$/)) {
        throw new Error(`Fila ${index + 2}: El período debe tener formato YYYYMM`);
      }

      if (isNaN(dias) || dias <= 0) {
        throw new Error(`Fila ${index + 2}: Los días deben ser un número positivo`);
      }

      datos.push({
        periodo,
        c_vend,
        c_tiponovedad,
        dias,
        observaciones
      });
    });

    return datos;
  }

  onSubmit(): void {
    if (this.novedadForm.invalid) {
      return;
    }

    const formData = this.novedadForm.value;
    const selectedPeriodo = formData.periodo;

    const novedadData = {
      periodo: selectedPeriodo.periodo,
      c_tiponovedad: formData.c_tiponovedad,
      c_ven: formData.c_ven,
      dias: parseInt(formData.dias, 10),
      observaciones: formData.observaciones || '',
      estado: 'A'
    };
    showSpinner();

    if (this.isEditing && this.editIndex !== null) {
      // Actualizar novedad existente
      this.novedadesComercialesService.updateNovedad(this.editIndex, novedadData)
        .pipe(finalize(() => hideSpinner()))
        .subscribe({
          next: (response) => {
            this.notificationService.successMessage('Novedad actualizada correctamente');
            this.resetForm();
            this.cargarNovedades();
          },
          error: (error) => {
            let errorMessage = 'Error al actualizar la novedad';
            if (error.error && error.error.detail) {
              errorMessage += ': ' + error.error.detail;
            }
            this.notificationService.errorMessage(errorMessage);
          }
        });
    } else {
      // Crear nueva novedad
      this.novedadesComercialesService.createNovedad(novedadData)
        .pipe(finalize(() => hideSpinner()))
        .subscribe({
          next: (response) => {
            this.notificationService.successMessage('Novedad creada correctamente');
            this.resetForm();
            this.cargarNovedades();
          },
          error: (error) => {
            let errorMessage = 'Error al crear la novedad';
            if (error.error && error.error.detail) {
              errorMessage += ': ' + error.error.detail;
            }
            this.notificationService.errorMessage(errorMessage);
          }
        });
    }
  }

  resetForm(): void {
    this.novedadForm.reset();
    this.isEditing = false;
    this.editIndex = null;
    this.vendedorControl.reset();
    this.vendedorControl.enable();
  }

  onDelete(id: number): void {
    const novedad = this.dataSource.data.find(n => n.id === id);
    if (!novedad) {
      this.notificationService.errorMessage('No se encontró la novedad');
      return;
    }

    if (!this.isPeriodoAbierto(novedad.periodo)) {
      this.notificationService.warningMessage('No se puede eliminar un registro de un periodo cerrado');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: { message: '¿Está seguro de eliminar esta novedad?' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        showSpinner();
        this.novedadesComercialesService.deleteNovedad(id)
          .pipe(finalize(() => hideSpinner()))
          .subscribe({
            next: () => {
              this.notificationService.successMessage('Novedad eliminada correctamente');
              this.cargarNovedades();
            },
            error: (error) => {
              this.notificationService.errorMessage('Error al eliminar la novedad');
            }
          });
      }
    });
  }

  onEdit(novedad: NovedadComercial): void {
    if (!this.isPeriodoAbierto(novedad.periodo)) {
      this.notificationService.warningMessage('No se puede editar un registro de un periodo cerrado');
      return;
    }

    this.isEditing = true;
    this.editIndex = novedad.id;

    // Buscar el vendedor correspondiente
    const vendedor = this.vendedores.find(v => v.codigo_vendedor === novedad.c_ven);

    // Establecer los valores del formulario
    this.novedadForm.patchValue({
      periodo: this.periodosAbiertos.find(p => p.periodo === novedad.periodo),
      c_tiponovedad: novedad.c_tiponovedad,
      c_ven: novedad.c_ven,
      dias: novedad.dias,
      observaciones: novedad.observaciones
    });

    // Establecer el vendedor en el control de vendedor
    if (vendedor) {
      this.vendedorControl.setValue(vendedor, { emitEvent: false });
    }

    // Desactivar el control del vendedor
    this.vendedorControl.disable();
  }

  verDetalle(novedad: NovedadComercial): void {
    const dialogRef = this.dialog.open(DetalleNovedadDialogComponent, {
      width: '700px',
      data: {
        novedad: novedad,
        tipoNovedadDescripcion: this.getTipoNovedadDescripcion(novedad.c_tiponovedad),
        vendedorNombre: this.getVendedorNombre(novedad.c_ven)
      }
    });
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  mostrarListaVendedores(): void {
    this.vendedoresFiltrados = of(this.vendedores);
  }

  getTipoNovedadDescripcion(codigo: string): string {
    const tipoNovedad = this.tiposNovedad.find(t => t.codigo === codigo);
    return tipoNovedad ? tipoNovedad.descripcion : codigo;
  }

  getVendedorNombre(codigoVendedor: string): string {
    const vendedor = this.vendedores.find(v => v.codigo_vendedor === codigoVendedor);
    return vendedor ? vendedor.nombre_vendedor.toUpperCase() : codigoVendedor;
  }

  isPeriodoAbierto(periodo?: string): boolean {
    if (!periodo) return false;
    return this.periodosAbiertos.some(p => p.periodo === periodo);
  }

  cancelarSeleccionArchivo() {
    this.selectedFile = null;
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  private generarCSV(resultados: any[]): void {
    if (!resultados || !Array.isArray(resultados)) {
      return;
    }

    try {

      // Definir las columnas del CSV
      const columnas = ['Periodo', 'Vendedor', 'Tipo Novedad', 'Días', 'Observaciones', 'Estado', 'Mensaje'];
      
      // Convertir los resultados a filas del CSV
      const filas = resultados.map(r => {
        const fila = [
          r.periodo || '',
          r.c_vend || '',
          r.c_tiponovedad || '',
          r.dias || '',
          (r.observaciones || '').replace(/,/g, ';'),
          r.estado || 'N/A',
          (r.mensaje || '').replace(/,/g, ';') // Reemplazar comas por punto y coma para evitar problemas con CSV
        ];
        return fila.join(',');
      });
      
      // Crear el contenido del CSV con BOM para Excel
      const BOM = '\uFEFF'; // Agregar BOM para que Excel reconozca caracteres especiales
      const contenidoCSV = BOM + [columnas.join(','), ...filas].join('\n');
      
      // Crear el blob y descargar
      const blob = new Blob([contenidoCSV], { type: 'text/csv;charset=utf-8' });
      
      // Crear el enlace de descarga
      const link = document.createElement('a');
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = `resultados_carga_${new Date().getTime()}.csv`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

    } catch (error) {
      this.notificationService.errorMessage('Error al generar el archivo de resultados');
    }
  }

  private convertToCSV(objArray: any[]): string {
    const header = Object.keys(objArray[0]).join(',') + '\n';
    const rows = objArray.map(obj => Object.values(obj).join(',')).join('\n');
    return header + rows;
  }

  private parseCSV(fileContent: string): NovedadComercialBulkLoad[] {
    const datos: NovedadComercialBulkLoad[] = [];
    const filas = fileContent.split('\n');
    
    // Obtener los nombres de las columnas (primera fila)
    const columnas = filas[0].split(',').map(col => col.trim().toLowerCase());
    
    // Validar que estén todas las columnas requeridas
    const columnasRequeridas = ['periodo', 'c_vend', 'c_tiponovedad', 'dias', 'observaciones'];
    const columnasFaltantes = columnasRequeridas.filter(col => 
      !columnas.includes(col.toLowerCase())
    );

    if (columnasFaltantes.length > 0) {
      throw new Error(`El archivo debe contener las columnas: ${columnasRequeridas.join(', ')}`);
    }

    const periodos = new Set<string>();
    
    // Procesar cada fila
    for (let i = 1; i < filas.length; i++) {
      const fila = filas[i].split(',');
      if (fila.length !== columnas.length) continue;
      
      const periodo = fila[columnas.indexOf('periodo')].trim();
      const c_vend = fila[columnas.indexOf('c_vend')].trim().toUpperCase();
      const c_tiponovedad = fila[columnas.indexOf('c_tiponovedad')].trim().toUpperCase();
      const dias = Number(fila[columnas.indexOf('dias')].trim());
      const observaciones = fila[columnas.indexOf('observaciones')].trim();
      
      // Validar los datos
      if (!periodo || !c_vend || !c_tiponovedad || !dias) {
        throw new Error(`Fila ${i + 1}: Faltan campos requeridos`);
      }
      
      if (!periodo.match(/^\d{6}$/)) {
        throw new Error(`Fila ${i + 1}: El período debe tener formato YYYYMM`);
      }
      
      if (isNaN(dias) || dias <= 0) {
        throw new Error(`Fila ${i + 1}: Los días deben ser un número positivo`);
      }

      periodos.add(periodo);
      
      datos.push({
        periodo,
        c_vend,
        c_tiponovedad,
        dias,
        observaciones
      });
    }

    // Validar que solo haya un periodo
    if (periodos.size > 1) {
      throw new Error('Solo se permite el cargue de un periodo por vez');
    }

    // Obtener el periodo del archivo
    const periodo = Array.from(periodos)[0];

    // Validar que el periodo exista y no esté cerrado
    const periodoExistente = this.periodosAbiertos.find(p => p.periodo === periodo);
    if (!periodoExistente) {
      throw new Error(`El periodo ${periodo} no existe o está cerrado`);
    }
    
    return datos;
  }

  async procesarArchivo(): Promise<void> {
    if (!this.selectedFile) return;

    try {
      let datos: NovedadComercialBulkLoad[];
      if (this.selectedFile.name.endsWith('.csv')) {
        datos = await this.leerArchivoCSV(this.selectedFile);
      } else {
        const workbook = await this.leerArchivoExcel(this.selectedFile);
        datos = this.procesarDatosExcel(workbook);
      }

      if (!datos || !datos.length) {
        this.notificationService.warningMessage('El archivo debe contener datos válidos');
        return;
      }

      // Obtener el período del primer registro
      const periodo = datos[0].periodo;

      // Mostrar diálogo de opciones
      const dialogRef = this.dialog.open(OpcionesCargaDialogComponent, {
        width: '600px',
        disableClose: true,
        data: {
          nombreArchivo: this.selectedFile.name,
          periodo: periodo
        }
      });

      const result = await dialogRef.afterClosed().toPromise();

      if (!result || !result.confirmado) {
        return;
      }

      // Mostrar spinner DESPUÉS de la confirmación del usuario
      showSpinner('Procesando archivo...');

      try {
        // Procesar según la opción seleccionada
        if (result.tipo === 'REEMPLAZAR') {
          const response = await this.novedadesComercialesService.bulkLoad('JA',datos, true, periodo).toPromise();
          
          if (response.registrosFallidos && response.registrosFallidos.length > 0) {
            this.generarCSV(response.registrosFallidos);
          }

          const mensaje = `Se procesaron ${response.registrosInsertados} registros correctamente${response.registrosFallidos?.length ? `. ${response.registrosFallidos.length} registros fallaron.` : ''}`;
          this.notificationService.successMessage(response.mensaje);
        } else {
          const response = await this.novedadesComercialesService.bulkLoad('JA',datos, false).toPromise();
          
          if (response.registrosFallidos && response.registrosFallidos.length > 0) {
            this.generarCSV(response.registrosFallidos);
          }

          const mensaje = `Se procesaron ${response.registrosInsertados} registros correctamente${response.registrosFallidos?.length ? `. ${response.registrosFallidos.length} registros fallaron.` : ''}`;
          this.notificationService.successMessage(mensaje);
        }

        // Limpiar selección y recargar datos
        this.selectedFile = null;
        await this.cargarNovedades();

      } catch (error: any) {
        this.notificationService.errorMessage(
          error.error?.detail || error.message || 'No se pueden asignar registros a un periodo CERRADO'
        );
      } finally {
        hideSpinner();
      }
    } catch (error: any) {
      this.notificationService.errorMessage(
        error.error?.detail || error.message || 'Error desconocido'
      );
    }
  }

  leerArchivoCSV(file: File): Promise<NovedadComercialBulkLoad[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        try {
          const datos: NovedadComercialBulkLoad[] = [];
          const contenido = reader.result as string;
          const filas = contenido.split('\n');
          
          // Obtener los nombres de las columnas (primera fila)
          const columnas = filas[0].split(',').map(col => col.trim().toLowerCase());
          
          // Validar que estén todas las columnas requeridas
          const columnasRequeridas = ['periodo', 'c_vend', 'c_tiponovedad', 'dias', 'observaciones'];
          const columnasFaltantes = columnasRequeridas.filter(col => 
            !columnas.includes(col.toLowerCase())
          );

          if (columnasFaltantes.length > 0) {
            reject(new Error(`El archivo debe contener las columnas: ${columnasRequeridas.join(', ')}`));
            return;
          }
          
          // Procesar cada fila
          for (let i = 1; i < filas.length; i++) {
            const fila = filas[i].split(',');
            if (fila.length !== columnas.length) continue;
            
            const periodo = fila[columnas.indexOf('periodo')].trim();
            const c_vend = fila[columnas.indexOf('c_vend')].trim().toUpperCase();
            const c_tiponovedad = fila[columnas.indexOf('c_tiponovedad')].trim().toUpperCase();
            const dias = Number(fila[columnas.indexOf('dias')].trim());
            const observaciones = fila[columnas.indexOf('observaciones')].trim();
            
            // Validar los datos
            if (!periodo || !c_vend || !c_tiponovedad || !dias) {
              reject(new Error(`Fila ${i + 1}: Faltan campos requeridos`));
              return;
            }
            
            if (!periodo.match(/^\d{6}$/)) {
              reject(new Error(`Fila ${i + 1}: El período debe tener formato YYYYMM`));
              return;
            }
            
            if (isNaN(dias) || dias <= 0) {
              reject(new Error(`Fila ${i + 1}: Los días deben ser un número positivo`));
              return;
            }
            
            datos.push({
              periodo,
              c_vend,
              c_tiponovedad,
              dias,
              observaciones
            });
          }
          
          resolve(datos);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      reader.readAsText(file);
    });
  }

  leerArchivoExcel(file: File): Promise<XLSX.WorkBook> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const workbook = XLSX.read(reader.result, { type: 'binary' });
          resolve(workbook);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Error al leer el archivo Excel'));
      reader.readAsBinaryString(file);
    });
  }

  procesarDatosExcel(workbook: XLSX.WorkBook): NovedadComercialBulkLoad[] {
    // Obtener la primera hoja
    const primeraHoja = workbook.Sheets[workbook.SheetNames[0]];
    
    // Convertir a JSON con tipado
    const rows = XLSX.utils.sheet_to_json(primeraHoja, { 
      header: 0, 
      raw: false,
      defval: '' // valor por defecto para celdas vacías
    }) as Record<string, string>[];
    
    if (rows.length === 0) {
      throw new Error('El archivo está vacío');
    }
    
    // Obtener nombres de columnas (primera fila)
    const columnas = Object.keys(rows[0]);
    
    // Validar columnas requeridas
    const columnasRequeridas = ['periodo', 'c_vend', 'c_tiponovedad', 'dias', 'observaciones'];
    const columnasFaltantes = columnasRequeridas.filter(col => 
      !columnas.map(c => c.toLowerCase()).includes(col.toLowerCase())
    );
    
    if (columnasFaltantes.length > 0) {
      throw new Error(`El archivo debe contener las columnas: ${columnasRequeridas.join(', ')}`);
    }

    // Validar que solo haya un periodo
    const periodos = new Set(rows.map(row => String(row.periodo || '').trim()));
    if (periodos.size > 1) {
      throw new Error('Solo se permite el cargue de un periodo por vez');
    }

    // Obtener el periodo del archivo
    const periodo = Array.from(periodos)[0];

    // Validar que el periodo exista y no esté cerrado
    const periodoExistente = this.periodosAbiertos.find(p => p.periodo === periodo);
    if (!periodoExistente) {
      throw new Error(`El periodo ${periodo} no existe o está cerrado`);
    }
    
    // Procesar cada fila
    return rows.map((row, index) => {
      const periodo = String(row.periodo || '').trim();
      const c_vend = String(row.c_vend || '').trim().toUpperCase();
      const c_tiponovedad = String(row.c_tiponovedad || '').trim().toUpperCase();
      const dias = Number(row.dias);
      const observaciones = String(row.observaciones || '').trim();
      
      // Validaciones
      if (!periodo || !c_vend || !c_tiponovedad || !dias) {
        throw new Error(`Fila ${index + 2}: Faltan campos requeridos`);
      }
      
      if (!periodo.match(/^\d{6}$/)) {
        throw new Error(`Fila ${index + 2}: El período debe tener formato YYYYMM`);
      }
      
      if (isNaN(dias) || dias <= 0) {
        throw new Error(`Fila ${index + 2}: Los días deben ser un número positivo`);
      }
      
      return {
        periodo,
        c_vend,
        c_tiponovedad,
        dias,
        observaciones
      };
    });
  }
}
